<template>
  <div class="d-flex flex-column ga-6 pa-6" data-cc="bekki-empty-state">
    <img
      :src="bekkiDepotIllustration"
      alt=""
      width="109"
      class="h-auto align-self-start"
    />

    <div class="d-flex flex-column ga-4">
      <div class="d-flex flex-column ga-2">
        <p
          v-if="depotName"
          class="text-body-2 font-weight-medium text-primary text-uppercase"
          data-cc="bekki-empty-state-depot"
        >
          {{ t('widgets.bekki.empty.eyebrow', { name: depotName }) }}
        </p>

        <div class="d-flex flex-column ga-3">
          <h2 class="text-h4" data-cc="bekki-empty-state-greeting">
            {{ t('widgets.bekki.empty.greeting', { name: firstname }) }}
          </h2>

          <p
            v-if="scopeLabel"
            class="text-h5"
            data-cc="bekki-empty-state-question"
          >
            {{ t('widgets.bekki.empty.question', { scope: scopeLabel }) }}
          </p>

          <p class="text-body-1 text-disabled">
            {{ t('widgets.bekki.empty.hint') }}
          </p>
        </div>
      </div>

      <div class="d-flex flex-column ga-4" data-cc="bekki-suggestions">
        <p class="text-caption text-disabled text-uppercase">
          {{ t('widgets.bekki.empty.suggestionsLabel') }}
        </p>

        <div class="d-flex flex-column ga-2">
          <v-btn
            v-for="(key, index) in SUGGESTION_KEYS"
            :key="key"
            variant="text"
            height="auto"
            rounded="xl"
            block
            class="bekki-empty-state__suggestion border justify-start px-4 py-3"
            data-cc="bekki-suggestion"
            :data-item="index"
            @click="bekkiChatStore.send(t(key))"
          >
            <span class="text-body-1 text-start text-wrap">{{ t(key) }}</span>
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { useI18n } from 'vue-i18n';

import bekkiDepotIllustration from '@/assets/svg/bekki-depot.svg';
import { useUserProfile } from '@/composables/useUserProfile';
import constants from '@/config/constants';
import useBekkiChatStore from '@/stores/bekkiChat';
import useScopesStore from '@/stores/scopes';

const { t } = useI18n();

const { firstname } = useUserProfile();
const bekkiChatStore = useBekkiChatStore();
const scopesStore = useScopesStore();

const { DEPOT, SUBCONTRACTOR, TENANT } = constants.scopeTypes;

const SUGGESTION_KEYS = [
  'widgets.bekki.empty.suggestion1',
  'widgets.bekki.empty.suggestion2',
  'widgets.bekki.empty.suggestion3'
] as const;

const depotName = computed((): string | null => {
  const { type, data } = scopesStore.selectedScope;
  if (type !== DEPOT || !data) return null;
  // Same label as the depot selector.
  return `${data.externalDepotId} ${data.locationName}`;
});

const scopeLabel = computed((): string | null => {
  const { type } = scopesStore.selectedScope;
  if (type === DEPOT) return t('widgets.bekki.scope.depot');
  if (type === TENANT) return t('widgets.bekki.scope.tenant');
  if (type === SUBCONTRACTOR) return t('widgets.bekki.scope.subcontractor');
  return null;
});
</script>

<style scoped>
.bekki-empty-state__suggestion {
  transition:
    border-color 120ms ease,
    background-color 120ms ease;
}

.bekki-empty-state__suggestion:hover {
  --v-border-color: var(--v-theme-primary);
  --v-border-opacity: 1;
  background-color: rgba(var(--v-theme-primary), var(--v-opacity-softer));
}

.bekki-empty-state__suggestion:hover :deep(.v-btn__overlay),
.bekki-empty-state__suggestion:active :deep(.v-btn__overlay) {
  --v-hover-opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .bekki-empty-state__suggestion {
    transition: none;
  }
}
</style>
